import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import Api from '../utils/Api';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';
  
const ExpenseCategoryContext = createContext();

export const useExpenseCategory = () => {
  const context = useContext(ExpenseCategoryContext);
  if (!context) {
    throw new Error('useExpenseCategory must be used within a ExpenseCategoryProvider');
  }
  return context;
};

export const ExpenseCategoryProvider = ({ children }) => {
   
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { user, getToken } = useAuth();
  const [api, setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [expenseCategories, setExpenseCategories] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/expense-categories');

  const getExpenseCategories = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if (filters?.q) {
        query = '&q=' + filters?.q;
      } else {
        const q = searchParams.get('q') || '';
        query = '&q=' + q;
      }
      if (filters?.active) {
        query += '&active=' + filters?.active;
      } else {
        const active = searchParams.get('active') || '';
        query += '&active=' + active;
      }

      let response = await apiInstance.call('expense-categories?page=' + page + '&paginate=' + paginate + '' + query, 'GET');
      if (filters?.all) {
        query = '';
        setPaginate(false);
        response = await apiInstance.call('expense-categories');
      }
 
      if (response.status === 200) {
        setPaginate(response.data.paginate);
        setExpenseCategories(response.data?.expenseCategories || []);
        hideLoading();
        if (filters) {
          return response.data.expenseCategories;
        }
      } else {
        hideLoading();
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch expense categories'
        });
      }
    } catch (error) {
      hideLoading();
    }
  }, [handleApiError, hideLoading, paginate, getToken]);

  const getExpenseCategoryByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`expense-categories/${id}`, 'GET', null, true, true);

    if (response.status === 200) {
      let expenseCategories = { 'data': [response.data.expense_category] };
      setExpenseCategories(expenseCategories || []);
    }
  }, [getToken]);

  useEffect(() => {
    const handleLoad = async () => {
      const currentPath = window.location.pathname;

      if (currentPath.includes(getRoutePath('/expense-categories/edit/' + id))) {
        if (getToken()) {
          const apiInstance = Api(() => getToken());
          setApi(apiInstance);
          await getExpenseCategoryByIdApi(id);
        }
      }
    };

    window.addEventListener('load', handleLoad);
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getExpenseCategoryByIdApi, searchParams]);

  useEffect(() => {
    const initializeApi = async () => {
      try {
        const apiInstance = Api(() => getToken());
        setApi(apiInstance);

        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getExpenseCategories(page);
        }
      } catch (error) {
        // Handle error
      }
    };

    if (initialized) {
      initializeApi();
    }
  }, [getToken, getExpenseCategories, searchParams, initialized]);

  const addExpenseCategory = async (category) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(category).forEach(key => {
      if (category[key] !== null) {
        formData.append(key, category[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call('expense-categories', 'POST', formData, true, true);
      
      if (response.status === 200) {
        const expenseCategories = response.data.expenseCategories;
        setExpenseCategories(expenseCategories);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add expense category'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to add expense category'
      });
    }
  };

  const getExpenseCategoryById = (id) => {
    let expenseCategoryData = expenseCategories?.data?.find(category => {
      return Number(category.id) === Number(id);
    });
    return expenseCategoryData;
  };

  const updateExpenseCategory = async (id, updatedCategory) => {
    if (!api) {
      return;
    }

    const formData = new FormData();
    Object.keys(updatedCategory).forEach(key => {
      if (updatedCategory[key] !== null) {
        formData.append(key, updatedCategory[key]);
      }
    });
    formData.append('company_id', user?.current_company?.id);
    formData.append('user_id', user?.id);

    try {
      const response = await api.call(`expense-categories/${id}`, 'POST', formData, true, true);
      if (response.status === 200) {
        const updatedExpenseCategories = expenseCategories?.data?.map(category =>
          Number(category.id) === Number(id) ? {
            ...response.data.expense_category,
          } : category
        );
        setExpenseCategories(updatedExpenseCategories);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update expense category'
          });
        }
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to update expense category'
      });
    }
  };

  const deleteExpenseCategory = async (id) => {
    if (!api) {
      return;
    }

    try {
      const response = await api.call(`expense-categories/${id}`, 'DELETE', null, true);
      if (response.status === 200) {
        navigate(getRoutePath('/expense-categories'));
        return { status: response.status };
      } else {
        return handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to delete expense category'
        });
      }
    } catch (error) {
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete expense category'
      });
    }
  };

  return (
    <ExpenseCategoryContext.Provider
      value={{
        expenseCategories,
        addExpenseCategory,
        getExpenseCategories: (page, filters) => getExpenseCategories(page, filters),
        getExpenseCategoryById,
        updateExpenseCategory,
        deleteExpenseCategory,
        formErrors,
        paginate,
        clearErrors
      }}
    >
      {children}
    </ExpenseCategoryContext.Provider>
  );
}; 